// App.js — Main application entry point with Clerk authentication
// UPDATED Feb 28 2026: Added My Reports route
// UPDATED Apr 2026: Branded login — teal background, white jetpack logo
// UPDATED May 2026: Added ENQ Discovery Report route (/project/:projectId/discovery)
// UPDATED May 2026: Added Campaign Builder route (/project/:projectId/campaign-builder)
// UPDATED Jun 2026: Added Project Scorecard route (/project/:projectId/scorecard)
// UPDATED Jun 2026: Home route now shows Projects List page
// UPDATED Jul 2026: Added onboarding gate — /onboarding (org creation) and /project/new (project creation + ENQ)
// UPDATED Jul 25 2026: OnboardingPage now uses useUser() hook directly instead of prop drilling
// UPDATED Jul 26 2026: Fixed schema mismatch (organisations/org_members vs organizations/user_organizations)
// UPDATED Jul 27 2026: Added onOrgCreated callback to OnboardingPage to update hasOrg before redirect
// UPDATED Aug 18 2026: Added Entity Bank Prompt Generator route
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useNavigate } from 'react-router-dom';
import { ClerkProvider, SignIn, SignUp, useUser, UserButton } from '@clerk/clerk-react';
import CampaignSetup from './CampaignSetup';
import CampaignListPage from './components/CampaignListPage';
// Import page components
import MyPromptsPage from './pages/MyPromptsPage';
import MyURLsPage from './pages/MyURLsPage';
import MySitePage from './pages/MySitePage';
import CampaignSummary from './pages/CampaignSummary';
import MyReportsPage from './pages/MyReportsPage';
import SettingsPage from './pages/SettingsPage';
import DiscoveryReport from './pages/DiscoveryReport';
import SidebarLayout from './components/SidebarLayout';
import CampaignBuilder from './pages/CampaignBuilder';
import ProjectScorecard from './components/ProjectScorecard';
import ProjectsListPage from './pages/ProjectsListPage';
import OnboardingPage from './pages/OnboardingPage';
import ProjectNew from './pages/ProjectNew';
import EntityBankPromptGenerator from './pages/EntityBankPromptGenerator';

const API_BASE_URL = `${process.env.REACT_APP_API_URL || 'https://geojetpack-production.up.railway.app'}/api`;

// Get Clerk publishable key from environment
const clerkPubKey = process.env.REACT_APP_CLERK_PUBLISHABLE_KEY;

// ── Clerk appearance config — brand teal primary, white-on-teal button text
const clerkAppearance = {
  variables: {
    colorPrimary: '#2a8ba8',
    colorTextOnPrimaryBackground: '#ffffff',
    colorBackground: '#ffffff',
    colorText: '#39393a',
    colorInputBackground: '#ffffff',
    colorInputText: '#39393a',
    borderRadius: '10px',
    fontFamily: '"Work Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  },
  elements: {
    formButtonPrimary: {
      backgroundColor: '#2a8ba8',
      fontWeight: 600,
      '&:hover': { backgroundColor: '#226f87' },
      '&:focus': { backgroundColor: '#226f87' },
    },
    card: {
      boxShadow: '0 10px 40px rgba(0, 0, 0, 0.25)',
      border: 'none',
    },
    footerActionLink: {
      color: '#2a8ba8',
      fontWeight: 600,
      '&:hover': { color: '#226f87' },
    },
    identityPreviewEditButton: { color: '#2a8ba8' },
    formFieldLabel: { color: '#39393a', fontWeight: 500 },
  },
};

// ── Shared auth layout — teal background, white logo floating above form
const AuthLayout = ({ children }) => (
  <div
    style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: '#2a8ba8',
      padding: '24px',
    }}
  >
    <img
      src="/PNG_White_Jetpack_Logo_300dpi.png"
      alt="GEO Jetpack"
      style={{ height: '100px', marginBottom: '32px' }}
    />
    {children}
    <p
      style={{
        color: 'rgba(255,255,255,0.75)',
        fontFamily: '"Work Sans", sans-serif',
        fontSize: '13px',
        marginTop: '32px',
        letterSpacing: '0.02em',
      }}
    >
      Binary Bear &middot; GEO Jetpack
    </p>
  </div>
);

// ── Root App component
function App() {
  if (!clerkPubKey) {
    return <div>Missing Clerk Publishable Key</div>;
  }

  return (
    <ClerkProvider publishableKey={clerkPubKey} appearance={clerkAppearance}>
      <BrowserRouter>
        <Routes>
          {/* Public Routes - Login/Signup */}
          <Route
            path="/sign-in/*"
            element={
              <AuthLayout>
                <SignIn routing="path" path="/sign-in" signUpUrl="/sign-up" />
              </AuthLayout>
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <AuthLayout>
                <SignUp routing="path" path="/sign-up" signInUrl="/sign-in" />
              </AuthLayout>
            }
          />
          {/* Protected Routes - Require Authentication */}
          <Route path="/*" element={<ProtectedApp />} />
        </Routes>
      </BrowserRouter>
    </ClerkProvider>
  );
}

// ── Protected App - Only accessible when signed in
function ProtectedApp() {
  const { isSignedIn, user, isLoaded } = useUser();

  if (!isLoaded) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (!isSignedIn) {
    return <Navigate to="/sign-in" replace />;
  }

  return (
    <AuthenticatedApp
      userId={user.id}
      userEmail={user.primaryEmailAddress?.emailAddress}
    />
  );
}

// ── Main authenticated app
function AuthenticatedApp({ userId, userEmail }) {
  const [campaigns, setCampaigns] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasOrg, setHasOrg] = useState(null); // null = unknown, true/false once checked

  useEffect(() => {
    Promise.all([
      fetchCampaigns(userId),
      fetchOrgStatus(userId),
    ]).finally(() => setIsLoading(false));
  }, [userId]);

  // Fetch campaigns — used by SidebarLayout and campaign routes.
  // Does NOT drive the onboarding gate any more.
  const fetchCampaigns = async (uid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/campaigns`, {
        headers: { 'x-user-id': uid },
      });
      if (response.ok) {
        const data = await response.json();
        setCampaigns(data);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  // Real org check — drives the onboarding gate.
  // GET /api/org/status returns 200 if the user has an organisation, 404 if not.
  // Does NOT auto-create (unlike /api/org/mine).
  const fetchOrgStatus = async (uid) => {
    try {
      const response = await fetch(`${API_BASE_URL}/org/status`, {
        headers: { 'x-user-id': uid },
      });
      if (response.status === 200) {
        setHasOrg(true);
      } else if (response.status === 404) {
        setHasOrg(false);
      } else {
        // 5xx or unexpected — don't block existing users
        setHasOrg(true);
      }
    } catch (error) {
      console.error('Error fetching org status:', error);
      setHasOrg(true); // network error — fail open
    }
  };

  if (isLoading || hasOrg === null) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <div>Loading...</div>
      </div>
    );
  }

  return (
    <Routes>
      {/* ── Onboarding gate ──────────────────────────────────────────────── */}
      {/* Step 1: create organisation */}
      <Route
        path="/onboarding"
        element={<OnboardingPage apiBaseUrl={API_BASE_URL} onOrgCreated={() => setHasOrg(true)} />}
      />

      {/* Step 2: create first project + trigger ENQ */}
      <Route
        path="/project/new"
        element={
          <SidebarLayout campaigns={campaigns} apiBaseUrl={API_BASE_URL}>
            <ProjectNew apiBaseUrl={API_BASE_URL} userId={userId} />
          </SidebarLayout>
        }
      />

      {/* Home — redirect new users to onboarding, existing users to projects list */}
      <Route
        path="/"
        element={
          !hasOrg ? (
            <Navigate to="/onboarding" replace />
          ) : (
            <SidebarLayout campaigns={campaigns} apiBaseUrl={API_BASE_URL}>
              <ProjectsListPage apiBaseUrl={API_BASE_URL} userId={userId} />
            </SidebarLayout>
          )
        }
      />

      {/* Create New Campaign (legacy route) */}
      <Route
        path="/campaign/new"
        element={
          <CampaignSetupWrapper
            userId={userId}
            onCampaignReady={(campaign) => {
              window.location.href = `/campaign/${campaign.id}/summary`;
            }}
          />
        }
      />

      {/* Campaign Workflow Routes */}
      <Route
        path="/campaign/:campaignId/*"
        element={
          <CampaignRouter
            campaigns={campaigns}
            apiBaseUrl={API_BASE_URL}
            userId={userId}
          />
        }
      />

      {/* Project Scorecard — landing page when a user clicks a project */}
      <Route
        path="/project/:projectId/scorecard"
        element={
          <SidebarLayout campaigns={campaigns} apiBaseUrl={API_BASE_URL}>
            <ProjectScorecard apiBaseUrl={API_BASE_URL} userId={userId} />
          </SidebarLayout>
        }
      />

      {/* ENQ Discovery Report (screen 1b) */}
      <Route
        path="/project/:projectId/discovery"
        element={
          <SidebarLayout campaigns={campaigns} apiBaseUrl={API_BASE_URL}>
            <DiscoveryReport apiBaseUrl={API_BASE_URL} userId={userId} />
          </SidebarLayout>
        }
      />

      {/* Entity Bank Prompt Generator (optional workflow) */}
      <Route
        path="/project/:projectId/entity-bank-prompt-generator"
        element={
          <SidebarLayout campaigns={campaigns} apiBaseUrl={API_BASE_URL}>
            <EntityBankPromptGenerator apiBaseUrl={API_BASE_URL} userId={userId} />
          </SidebarLayout>
        }
      />

      {/* ENQ Campaign Builder (screen 2) */}
      <Route
        path="/project/:projectId/campaign-builder"
        element={<CampaignBuilder apiBaseUrl={API_BASE_URL} userId={userId} />}
      />

      {/* Organisation settings */}
      <Route
        path="/settings"
        element={<SettingsPage campaigns={campaigns} apiBaseUrl={API_BASE_URL} />}
      />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// ── Campaign Router — handles all campaign sub-routes
function CampaignRouter({ campaigns, apiBaseUrl, userId }) {
  const { campaignId } = useParams();
  const navigate = useNavigate();

  const currentCampaign = campaigns.find((c) => c.id === campaignId);

  if (!currentCampaign && campaignId !== 'new') {
    return <Navigate to="/" replace />;
  }

  return (
    <Routes>
      <Route index element={<Navigate to="summary" replace />} />
      <Route
        path="summary"
        element={
          <CampaignSummary
            campaignId={campaignId}
            onEdit={() => navigate(`/campaign/${campaignId}/edit`)}
          />
        }
      />
      <Route
        path="prompts"
        element={
          <MyPromptsPage
            campaign={currentCampaign}
            campaigns={campaigns}
            apiBaseUrl={apiBaseUrl}
            userId={userId}
          />
        }
      />
      <Route
        path="urls"
        element={
          <MyURLsPage
            campaign={currentCampaign}
            campaigns={campaigns}
            apiBaseUrl={apiBaseUrl}
            userId={userId}
          />
        }
      />
      <Route
        path="site"
        element={
          <MySitePage
            campaign={currentCampaign}
            campaigns={campaigns}
            apiBaseUrl={apiBaseUrl}
            userId={userId}
          />
        }
      />
      <Route
        path="reports"
        element={
          <MyReportsPage
            campaign={currentCampaign}
            campaigns={campaigns}
            apiBaseUrl={apiBaseUrl}
            userId={userId}
          />
        }
      />
    </Routes>
  );
}

// ── Campaign Setup Wrapper — legacy campaign creation
function CampaignSetupWrapper({ userId, onCampaignReady }) {
  const navigate = useNavigate();

  const styles = {
    container: { minHeight: '100vh', backgroundColor: '#f3f4f6' },
    header: { backgroundColor: '#39393a', padding: '24px', color: 'white' },
    headerTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '16px',
    },
    subtitle: { margin: 0, opacity: 0.8, fontSize: '14px' },
    headerActions: { display: 'flex', gap: '12px', alignItems: 'center' },
    backButton: {
      padding: '10px 16px',
      borderRadius: '6px',
      border: 'none',
      cursor: 'pointer',
      fontFamily: 'Helvetica Neue, Helvetica, Arial, sans-serif',
      fontWeight: '500',
      fontSize: '13px',
      transition: 'all 0.2s',
      backgroundColor: '#555',
      color: 'white',
    },
    content: { maxWidth: '1200px', margin: '0 auto', padding: '24px' },
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <div style={styles.headerTop}>
          <div>
            <img
              src="/logo.svg"
              alt="GEO Jetpack"
              style={{ height: '96px', marginBottom: '8px' }}
            />
            <p style={styles.subtitle}>Create New Campaign</p>
          </div>
          <div style={styles.headerActions}>
            <button onClick={() => navigate('/')} style={styles.backButton}>
              ← Back to Campaigns
            </button>
            <UserButton afterSignOutUrl="/sign-in" />
          </div>
        </div>
      </div>
      <div style={styles.content}>
        <CampaignSetup onCampaignReady={onCampaignReady} userId={userId} />
      </div>
    </div>
  );
}

export default App;
